import axios, {AxiosInstance, AxiosRequestConfig} from 'axios';
import { RootState, UserState } from '@/store/types';
import { XamarinAdapter } from '../XamarinAdapter';
import { Client as AccountClient } from './AccountApi';
import { Client as DeviceClient } from './DeviceApi';
import store from '@/store/index';
import IEventApi from '../events/api/IEventApi';
import XamarinEventApi from '../events/api/XamarinEventApi';
import EventApi from '../events/api/EventApi';
import Logger from '@/common/logging/Logger';

export class ApiProvider {
    private logger = Logger('api');
    private eventApi: IEventApi;
    private deviceApi: DeviceClient | null;
    private accountApi: AccountClient | null;
    private axiosInstance: AxiosInstance;

    public getEventApi(): IEventApi {
        return this.eventApi || (this.eventApi = XamarinAdapter.isXamarin()
            ? new XamarinEventApi()
            : new EventApi(this.getEventApiUrl()));
      }

    public getAccontApi(): AccountClient {
        return this.accountApi || (this.accountApi = new AccountClient(this.getAccountApiBaseUrl(), this.getAxios()));
    }

    public getDeviceApi(): DeviceClient {
        return this.deviceApi || (this.deviceApi = new DeviceClient(this.getDeviceApiBaseUrl(), this.getAxios()));
    }

    public clearBearerToken() {
        this.deviceApi = null;
        this.accountApi = null;
    }

    private getAxios(): AxiosInstance {
        const rootState = store.state;
        const userState = (rootState as any).user as UserState;
        if (userState && userState.jwtToken) {
            const ax = axios.create({
                headers: {
                    Authorization: (userState.jwtToken.startsWith('Bearer') ? '' : 'Bearer ') + userState.jwtToken,
                },
                adapter: XamarinAdapter.isXamarin() ? XamarinAdapter.instance.axiosAdapter : undefined,
            });
            ax.interceptors.request.use((config: AxiosRequestConfig) => {
                (config as any).metadata = {startTime: new Date() };
                this.logger.info(`Request ${config.method} ${config.url}`);
                // console.log('config', config);
                return config;
            });
            ax.interceptors.response.use((response) => {
                if (response.config.url) {
                    const duration = new Date().getTime() - ((response.config as any).metadata.startTime as Date).getTime();
                    const url = response.config.url.substring(response.config.url.indexOf('/', 8));
                    this.logger.info(`Ajax ${response.config.method} ${response.status}:${response.statusText} ${url} ${duration} ms`);
                }
                return response;
            }, (rejected) => {
                if (rejected.config.url) {
                    const duration = new Date().getTime() - ((rejected.config as any).metadata.startTime as Date).getTime();
                    const url = rejected.config.url.substring(rejected.config.url.indexOf('/', 8));
                    if (rejected.status) {
                        this.logger.error(`Ajax ${rejected.config.method} ${rejected.status}:${rejected.statusText} ${rejected.config.url} ${duration} ms: ${rejected.message}`);
                    } else {
                        this.logger.error(`Ajax ${rejected.config.method} ${url} ${duration} ms: ${rejected.message}`);
                    }
                }
                return Promise.reject(rejected);
            });
            return ax;
        }
        return axios.create();
    }
    private getDeviceApiBaseUrl(): string {
        if (location.host === 'app.boatcontrol.net') {
            return 'https://api.boatcontrol.net';
        }
        if (location.host.endsWith('app.boatcontrol.io')) {
            const base = location.host.substring(0, location.host.length - 'app.boatcontrol.io'.length);
            return 'https://' + base + 'api.boatcontrol.io';
        }
        return 'https://api.boatcontrol.net';
        //return 'http://localhost:51210/';
    }

    private getAccountApiBaseUrl(): string {
        if (location.host === 'app.boatcontrol.net') {
            return 'https://account.boatcontrol.net';
        }
        if (location.host.endsWith('account.boatcontrol.io')) {
            const base = location.host.substring(0, location.host.length - 'account.boatcontrol.io'.length);
            return 'https://' + base + 'account.boatcontrol.io';
        }
        // return 'http://localhost:53069';
        return 'https://account.boatcontrol.net';
    }

    private getEventApiUrl(): string {
        return this.getDeviceApiBaseUrl()
            .replace('https://', 'wss://')
            .replace('http://', 'ws://')
            + '/api/ws?access_token={token}';
    }

}

export default new ApiProvider();
